import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import {
  TrendingUp, Users, CreditCard, AlertCircle, Clock, Sparkles, Building2,
  ArrowUpRight, ArrowDownRight, Calculator, Download,
} from 'lucide-react';

const PLAN_COLORS = {
  start: '#5B8DEF',
  pro: '#E8613A',
  business: '#7A5AF8',
  custom: '#7A7570',
};

const PLAN_LABELS = {
  start: 'Start',
  pro: 'Pro',
  business: 'Business',
  custom: 'Custom',
};

const CONTRACT_LABELS = {
  6: '6 mån',
  12: '12 mån',
  24: '24 mån',
};

const fmtSek = (n) => `${Math.round(n || 0).toLocaleString('sv-SE')} kr`;

const KPI = ({ label, value, sub, icon: Icon, tone = 'neutral', testid }) => {
  const toneClass =
    tone === 'green' ? 'text-emerald-600 dark:text-emerald-400' :
    tone === 'red' ? 'text-rose-600 dark:text-rose-400' :
    tone === 'orange' ? 'text-[#E8613A]' :
    'text-foreground';
  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">{label}</p>
          <p className={`text-3xl font-bold tracking-tight mt-1 ${toneClass}`}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        {Icon && (
          <div className="p-2 bg-[#E8613A]/8 rounded-lg">
            <Icon size={16} className="text-[#E8613A]" />
          </div>
        )}
      </div>
    </div>
  );
};

const StackedBar = ({ data, totalCount }) => {
  if (!totalCount) return <div className="h-2 bg-muted rounded-full" />;
  return (
    <div className="flex h-3 rounded-full overflow-hidden bg-muted">
      {data.map((d, i) => {
        const pct = (d.count / totalCount) * 100;
        if (!pct) return null;
        return (
          <div key={i} style={{ width: `${pct}%`, background: d.color }} title={`${d.label}: ${d.count}`} />
        );
      })}
    </div>
  );
};

const SuperAdminSalesStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.saStatsOverview();
        setStats(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Kunde inte hämta statistik');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const planData = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.by_plan).map(([k, v]) => ({
      key: k, label: PLAN_LABELS[k] || k, count: v.count, mrr: v.mrr, color: PLAN_COLORS[k] || '#888',
    }));
  }, [stats]);

  const contractData = useMemo(() => {
    if (!stats) return [];
    return [6, 12, 24].map((m) => ({
      months: m, label: CONTRACT_LABELS[m], count: stats.by_contract[m]?.count || 0, mrr: stats.by_contract[m]?.mrr || 0,
    }));
  }, [stats]);

  const totalContractCount = useMemo(
    () => contractData.reduce((s, c) => s + c.count, 0),
    [contractData]
  );

  const totalPlanCount = useMemo(
    () => planData.reduce((s, c) => s + c.count, 0),
    [planData]
  );

  const exportCsv = () => {
    if (!stats?.pipeline) return;
    const header = ['Företag', 'Paket', 'Bindning (mån)', 'MRR (kr)', 'Status', 'Betalningssätt', 'Konto-typ', 'Skapad'];
    const rows = stats.pipeline.map((p) => [
      p.name || '',
      p.plan_label || p.plan_id || '',
      p.contract_months || '',
      Math.round(p.monthly_price || 0),
      p.subscription_status || (p.account_type === 'demo' ? 'demo' : '—'),
      p.billing_method || '',
      p.account_type || '',
      p.created_at ? new Date(p.created_at).toLocaleDateString('sv-SE') : '',
    ]);
    const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `operio-sales-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="sa-stats-loading">
      <div className="w-6 h-6 border-2 border-[#E8613A] border-t-transparent op-round rounded-full animate-spin" />
    </div>
  );

  if (!stats) return null;

  const conversionRate = stats.new_companies_30d > 0
    ? Math.round((stats.conversion_30d / stats.new_companies_30d) * 100)
    : 0;
  const churnRate = stats.companies_paying > 0
    ? Math.round((stats.churn_30d / Math.max(1, stats.companies_paying + stats.churn_30d)) * 100)
    : 0;

  return (
    <div className="space-y-6 max-w-7xl" data-testid="sa-stats-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp size={22} className="text-[#E8613A]" /> Försäljningsstatistik
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Aktuell MRR, konvertering och fördelning per paket &amp; bindningstid.
          </p>
        </div>
        <button
          onClick={exportCsv}
          className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-border text-sm font-medium hover:bg-muted transition-colors"
          data-testid="sa-stats-export-csv"
        >
          <Download size={14} /> Exportera CSV
        </button>
      </div>

      {/* Top KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          label="MRR (Betalande)"
          value={fmtSek(stats.mrr_total)}
          sub={`ARR ${fmtSek(stats.arr_total)}`}
          icon={CreditCard}
          tone="orange"
          testid="sa-kpi-mrr"
        />
        <KPI
          label="MRR Pipeline"
          value={fmtSek(stats.mrr_committed)}
          sub="Inkl. trials"
          icon={Sparkles}
          testid="sa-kpi-mrr-committed"
        />
        <KPI
          label="Aktiva trials"
          value={stats.companies_trial}
          sub={`Snitt ${stats.active_trial_days_avg} dagar kvar`}
          icon={Clock}
          testid="sa-kpi-trials"
        />
        <KPI
          label="Betalande kunder"
          value={stats.companies_paying}
          sub={`${stats.companies_total} totalt`}
          icon={Users}
          tone="green"
          testid="sa-kpi-paying"
        />
      </div>

      {/* Secondary row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          label="Nya senaste 30d"
          value={stats.new_companies_30d}
          sub={`${stats.conversion_30d} konverterade`}
          icon={ArrowUpRight}
          tone="green"
          testid="sa-kpi-new30"
        />
        <KPI
          label="Konvertering 30d"
          value={`${conversionRate} %`}
          sub="Trial → Betalande"
          icon={TrendingUp}
          tone={conversionRate >= 50 ? 'green' : 'orange'}
          testid="sa-kpi-conv"
        />
        <KPI
          label="Churn 30d"
          value={`${churnRate} %`}
          sub={`${stats.churn_30d} företag`}
          icon={ArrowDownRight}
          tone={churnRate > 5 ? 'red' : 'neutral'}
          testid="sa-kpi-churn"
        />
        <KPI
          label="Demo-konton"
          value={stats.companies_demo}
          sub={`${stats.companies_paused} pausade`}
          icon={AlertCircle}
          testid="sa-kpi-demo"
        />
      </div>

      {/* Plan + Contract breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By plan */}
        <div className="bg-card border border-border rounded-xl p-5" data-testid="sa-stats-by-plan">
          <h3 className="text-sm font-bold text-foreground mb-1 flex items-center gap-2">
            <Building2 size={14} /> Fördelning per paket
          </h3>
          <p className="text-xs text-muted-foreground mb-4">MRR och antal företag</p>
          <StackedBar
            data={planData.filter(p => p.count > 0).map(p => ({ label: p.label, count: p.count, color: p.color }))}
            totalCount={totalPlanCount}
          />
          <div className="space-y-3 mt-4">
            {planData.filter(p => p.count > 0).map((p) => (
              <div key={p.key} className="flex items-center gap-3" data-testid={`sa-plan-row-${p.key}`}>
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: p.color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-foreground">{p.label}</p>
                    <p className="text-sm font-bold text-foreground">{fmtSek(p.mrr)}</p>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-0.5">
                    <span>{p.count} företag</span>
                    <span>{totalPlanCount ? Math.round((p.count / totalPlanCount) * 100) : 0} %</span>
                  </div>
                </div>
              </div>
            ))}
            {!totalPlanCount && (
              <p className="text-sm text-muted-foreground text-center py-6">Inga företag att visa än</p>
            )}
          </div>
        </div>

        {/* By contract */}
        <div className="bg-card border border-border rounded-xl p-5" data-testid="sa-stats-by-contract">
          <h3 className="text-sm font-bold text-foreground mb-1 flex items-center gap-2">
            <Calculator size={14} /> Fördelning per bindningstid
          </h3>
          <p className="text-xs text-muted-foreground mb-4">Hur kunder väljer 6 / 12 / 24 mån</p>
          <div className="space-y-4 mt-2">
            {contractData.map((c) => {
              const pct = totalContractCount ? Math.round((c.count / totalContractCount) * 100) : 0;
              return (
                <div key={c.months} data-testid={`sa-contract-row-${c.months}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-sm font-semibold text-foreground">{c.label}</p>
                    <div className="text-right">
                      <p className="text-sm font-bold text-foreground">{fmtSek(c.mrr)}</p>
                      <p className="text-xs text-muted-foreground">{c.count} företag · {pct} %</p>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#E8613A] to-[#D4532F]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
            {!totalContractCount && (
              <p className="text-sm text-muted-foreground text-center py-6">Inga kontrakt registrerade än</p>
            )}
          </div>
        </div>
      </div>

      {/* Pipeline table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="sa-stats-pipeline">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-bold text-foreground">Pipeline — alla företag ({stats.pipeline.length})</h3>
          <p className="text-xs text-muted-foreground">Sorterat efter MRR</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-5 py-3">Företag</th>
                <th className="text-left px-3 py-3">Paket</th>
                <th className="text-left px-3 py-3">Bindning</th>
                <th className="text-right px-3 py-3">MRR</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-5 py-3">Skapad</th>
              </tr>
            </thead>
            <tbody>
              {[...stats.pipeline]
                .sort((a, b) => (b.monthly_price || 0) - (a.monthly_price || 0))
                .map((p) => (
                  <tr key={p.id} className="border-t border-border hover:bg-muted/30" data-testid={`sa-pipeline-row-${p.id}`}>
                    <td className="px-5 py-3 font-medium text-foreground">{p.name}</td>
                    <td className="px-3 py-3 text-muted-foreground">{p.plan_label || p.plan_id || '—'}</td>
                    <td className="px-3 py-3 text-muted-foreground">{p.contract_months ? `${p.contract_months} mån` : '—'}</td>
                    <td className="px-3 py-3 text-right font-semibold text-foreground">{fmtSek(p.monthly_price)}</td>
                    <td className="px-3 py-3">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        p.active === false ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                        p.account_type === 'demo' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                        p.subscription_status === 'trialing' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                        p.subscription_status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {p.active === false ? 'Pausad' :
                         p.account_type === 'demo' ? 'Demo' :
                         p.subscription_status === 'trialing' ? 'Trial' :
                         p.subscription_status === 'active' ? 'Aktiv' :
                         p.subscription_status === 'past_due' ? 'Förfallen' :
                         p.subscription_status || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-muted-foreground">
                      {p.created_at ? new Date(p.created_at).toLocaleDateString('sv-SE') : '—'}
                    </td>
                  </tr>
                ))}
              {stats.pipeline.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted-foreground py-10">Inga företag i pipeline ännu</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminSalesStats;
